import { errorFilledToastOptions, ibanLengths } from "../../../../constants"
import { lang } from "../../../../constants/languages"
import { PaymentMethod } from "../../../../types"
import { toast } from "react-toastify"
import { CountryType } from "./countryConfig"

const checkIbanValidity = (iban: string) => {
  const cleanedIban = iban.replace(/\s+/g, "")

  if (cleanedIban.length < 2) return lang.account.payment_methods.payment_form.error.short_iban

  const firstTwo = cleanedIban.slice(0, 2)

  const allowedLength = ibanLengths[firstTwo]

  if (!allowedLength) return lang.account.payment_methods.payment_form.error.country_code_invalid

  if (allowedLength > cleanedIban.length) return lang.account.payment_methods.payment_form.error.short_iban

  if (allowedLength < cleanedIban.length) return lang.account.payment_methods.payment_form.error.long_iban

  const rearranged = cleanedIban.slice(4) + cleanedIban.slice(0, 4)

  let numericIban = ""
  for (const char of rearranged) {
    const code = char.charCodeAt(0)
    numericIban += code >= 65 && code <= 90 ? code - 55 : char
  }
  let remainder = numericIban
  while (remainder.length > 2) {
    const block = remainder.slice(0, 9)
    remainder = (parseInt(block, 10) % 97) + remainder.slice(block.length)
  }

  if (!(parseInt(remainder, 10) % 97 === 1)) return lang.account.payment_methods.payment_form.error.invalid_iban

  return null
}

const paymentMethodValidator = (formValues: PaymentMethod, countryType: CountryType) => {
  const errors: any = {}

  if (!formValues.accountHolderName) {
    errors.accountHolderName = lang.account.payment_methods.payment_form.error.account_holder_name_blank
    toast.error(lang.account.payment_methods.payment_form.error.account_holder_name_blank, errorFilledToastOptions)
  }

  if (!formValues.currency) {
    errors.currency = lang.account.payment_methods.payment_form.error.currency_select
    toast.error(lang.account.payment_methods.payment_form.error.currency_select, errorFilledToastOptions)
  }

  switch (countryType) {
    case "us": {
      const routingClean = (formValues.bankCode || "").replace(/\D/g, "")
      if (routingClean.length !== 9) {
        errors.bankCode = "Routing number must be exactly 9 digits"
        toast.error("Routing number must be exactly 9 digits", errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = "Account number is required"
        toast.error("Account number is required", errorFilledToastOptions)
      }
      break
    }

    case "ng": {
      const nuban = (formValues.accountNumber || "").replace(/\D/g, "")
      if (nuban.length !== 10) {
        errors.accountNumber = "Account number must be exactly 10 digits (NUBAN)"
        toast.error("Account number must be exactly 10 digits (NUBAN)", errorFilledToastOptions)
      }
      if (!formValues.bankName) {
        errors.bankName = "Please select a bank"
        toast.error("Please select a bank", errorFilledToastOptions)
      }
      break
    }

    case "za": {
      const branchCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (branchCode.length !== 6) {
        errors.bankCode = "Branch code must be exactly 6 digits"
        toast.error("Branch code must be exactly 6 digits", errorFilledToastOptions)
      }
      if (!formValues.bankName) {
        errors.bankName = "Please select a bank"
        toast.error("Please select a bank", errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = "Account number is required"
        toast.error("Account number is required", errorFilledToastOptions)
      }
      if (!formValues.accountType) {
        errors.accountType = "Account type is required"
        toast.error("Account type is required", errorFilledToastOptions)
      }
      break
    }

    case "au": {
      const bsb = (formValues.bankCode || "").replace(/\D/g, "")
      if (bsb.length !== 6) {
        errors.bankCode = "BSB must be exactly 6 digits"
        toast.error("BSB must be exactly 6 digits", errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = "Account number is required"
        toast.error("Account number is required", errorFilledToastOptions)
      }
      break
    }

    case "uk": {
      const sortCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (sortCode.length !== 6) {
        errors.bankCode = "Sort code must be exactly 6 digits"
        toast.error("Sort code must be exactly 6 digits", errorFilledToastOptions)
      }
      if (!formValues.accountNumber || formValues.accountNumber.length !== 8) {
        errors.accountNumber = "Account number must be exactly 8 digits"
        toast.error("Account number must be exactly 8 digits", errorFilledToastOptions)
      }
      if (!formValues.bankName) {
        errors.bankName = lang.account.payment_methods.payment_form.error.bank_name_blank
        toast.error(lang.account.payment_methods.payment_form.error.bank_name_blank, errorFilledToastOptions)
      }
      break
    }

    case "tr": {
      if (!formValues.iban) {
        errors.iban = lang.account.payment_methods.payment_form.error.iban_blank
        toast.error(lang.account.payment_methods.payment_form.error.iban_blank, errorFilledToastOptions)
      } else {
        const errorMessage = checkIbanValidity(formValues.iban)
        if (errorMessage !== null) {
          errors.iban = errorMessage
          toast.error(errorMessage, errorFilledToastOptions)
        }
      }
      if (!formValues.bankName) {
        errors.bankName = lang.account.payment_methods.payment_form.error.bank_name_blank
        toast.error(lang.account.payment_methods.payment_form.error.bank_name_blank, errorFilledToastOptions)
      }
      break
    }

    case "eu":
    default: {
      if (!formValues.iban) {
        errors.iban = lang.account.payment_methods.payment_form.error.iban_blank
        toast.error(lang.account.payment_methods.payment_form.error.iban_blank, errorFilledToastOptions)
      } else {
        const errorMessage = checkIbanValidity(formValues.iban)
        if (errorMessage !== null) {
          errors.iban = errorMessage
          toast.error(errorMessage, errorFilledToastOptions)
        }
      }
      if (!formValues.bankName) {
        errors.bankName = lang.account.payment_methods.payment_form.error.bank_name_blank
        toast.error(lang.account.payment_methods.payment_form.error.bank_name_blank, errorFilledToastOptions)
      }
      break
    }
  }

  return errors
}

export default paymentMethodValidator
